import DateFnsUtils from "@date-io/date-fns"
import {
    Button,
    FormControl,
    Grid,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    Tooltip,
} from "@material-ui/core"
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers"
import { ptBR } from "date-fns/locale"
import { useSnackbar, VariantType } from "notistack"
import React, { useMemo, useState } from "react"
import useLoadingStore from "../../stores/LoadingStore"

import {
    buscarConfiguracoesAgendamento,
    buscarPeriodosDeAgendamentoPedidos,
} from "../../services/apiAgendamento"

const useStyles = makeStyles(theme => ({
    agendar: {
        backgroundColor: "#1976D2",
        color: "white",
        "&:hover": { backgroundColor: "#1565C0" },
        marginBottom: "0.8rem",
    },
    cancelar: {
        backgroundColor: "#D32F2F",
        color: "white",
        "&:hover": { backgroundColor: "#B71C1C" },
        marginBottom: "0.8rem",
    },
}))

type ConfigAgendamento = {
    tempoMinimoAgendamentoHoras?: number | null
    tempoMaximoAgendamentoDias?: number | null
}

type Slot = {
    inicio_minutos: number
    fim_minutos: number
    esgotado?: boolean
    disponivel?: boolean
}

type Periodo = {
    id: string | number
    slots?: Slot[]
}

const startOfDay = (d: Date) => {
    const x = new Date(d)
    x.setHours(0, 0, 0, 0)
    return x
}

const addHours = (d: Date, hours: number) => {
    const x = new Date(d)
    x.setHours(x.getHours() + hours)
    return x
}

const addDays = (d: Date, days: number) => {
    const x = new Date(d)
    x.setDate(x.getDate() + days)
    return x
}

const formatDateDDMMAAAA = (d: Date) => {
    const dd = String(d.getDate()).padStart(2, "0")
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const yyyy = d.getFullYear()
    return `${dd}-${mm}-${yyyy}`
}

const calcularMinDate = (tempoMinimoAgendamentoHoras?: number | null) => {
    const minimo = tempoMinimoAgendamentoHoras ?? 0
    const agora = new Date()
    const earliest = addHours(agora, minimo)
    return startOfDay(earliest)
}

const calcularMaxDate = (tempoMaximoAgendamentoDias?: number | null) => {
    const max = tempoMaximoAgendamentoDias ?? 0
    const hoje = startOfDay(new Date())
    return startOfDay(addDays(hoje, max))
}

const getTime = (time: number) => {
    let hour = Math.floor(time / 60).toString()
    if (hour.length === 1) hour = "0" + hour

    let minute = (time % 60).toString()
    if (minute.length === 1) minute = "0" + minute

    return hour + ":" + minute
}

export function AgendamentoForm({ carrinho, setCarrinho }: any) {
    const classes = useStyles()
    const setLoading = useLoadingStore(s => s.setLoading)
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: VariantType | undefined) => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    const horas = new Date().getHours()
    const minutosAtuais = new Date().getMinutes()
    const totalMinutosAgora = horas * 60 + minutosAtuais

    const [configuracoes, setConfiguracoes] = useState<ConfigAgendamento | null>(null)
    const [minDate, setMinDate] = useState<Date>(() => startOfDay(new Date()))
    const [maxDate, setMaxDate] = useState<Date>(() => startOfDay(addDays(new Date(), 7)))

    const [isAgendamento, setIsAgendamento] = useState(
        !!carrinho?.dataAgendamento && !!carrinho?.slot_inicio
    )

    const [selectedDate, setSelectedDate] = useState<Date | null>(null)
    const [slotSelecionado, setSlotSelecionado] = useState<string>("")
    const [periodos, setPeriodos] = useState<Periodo[]>([])

    const slotsPossiveis = useMemo(() => {
        const opts: { id: string; inicio_minutos: number; fim_minutos: number }[] = []

        for (const p of periodos || []) {
            for (const s of p.slots || []) {
                if (s.esgotado) continue
                if (s.disponivel === false) continue

                opts.push({
                    id: `${p.id}:${s.inicio_minutos}-${s.fim_minutos}`,
                    inicio_minutos: s.inicio_minutos,
                    fim_minutos: s.fim_minutos,
                })
            }
        }

        opts.sort((a, b) => a.inicio_minutos - b.inicio_minutos)
        return opts
    }, [periodos])

    const handleBuscarConfiguracoes = async () => {
        try {
            setLoading(true)
            const idEstabelecimento = carrinho?.estabelecimento?.id
            if (!idEstabelecimento) {
                alertStart("Estabelecimento não encontrado no carrinho", "error")
                return
            }

            const response: ConfigAgendamento = await buscarConfiguracoesAgendamento(
                idEstabelecimento
            )
            setConfiguracoes(response)

            const min = calcularMinDate(response?.tempoMinimoAgendamentoHoras)
            const max = calcularMaxDate(response?.tempoMaximoAgendamentoDias)

            setMinDate(min)
            setMaxDate(max)

            setSelectedDate(prev => {
                if (!prev) return prev
                const d = startOfDay(prev)
                if (d < min) return min
                if (d > max) return max
                return prev
            })
        } catch (e) {
            console.log(e)
            alertStart("Erro ao buscar configurações de agendamento", "error")
        } finally {
            setLoading(false)
        }
    }

    const handleBuscarPeriodos = async (data: Date) => {
        try {
            setLoading(true)
            const idEstabelecimento = carrinho?.estabelecimento?.id
            if (!idEstabelecimento) {
                alertStart("Estabelecimento não encontrado no carrinho", "error")
                return
            }

            const dataFormatada = formatDateDDMMAAAA(data)
            const response = await buscarPeriodosDeAgendamentoPedidos(
                dataFormatada,
                idEstabelecimento
            )

            if (Array.isArray(response)) setPeriodos(response)
            else setPeriodos([])
        } catch (e) {
            console.log(e)
            setPeriodos([])
            alertStart("Erro ao buscar período de agendamento", "error")
        } finally {
            setLoading(false)
        }
    }

    const handleChangeSlot = (e: React.ChangeEvent<{ value: unknown }>) => {
        const slotId = e.target.value as string
        setSlotSelecionado(slotId)

        if (!selectedDate) return

        const slot = slotsPossiveis.find(s => s.id === slotId)
        if (!slot) return

        const dataFormatada = formatDateDDMMAAAA(selectedDate)

        setCarrinho({
            ...carrinho,
            dataAgendamento: dataFormatada,
            slot_inicio: slot.inicio_minutos,
        })
    }

    return (
        <div
            style={{ display: "flex", width: "100%", flexDirection: "column", marginTop: "0.8rem" }}
        >
            {isAgendamento && (
                <Grid container alignItems="center" spacing={1}>
                    <Grid item xs={12} sm={6} md={6}>
                        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
                            <KeyboardDatePicker
                                fullWidth
                                disableToolbar
                                variant="inline"
                                format="dd/MM/yyyy"
                                margin="normal"
                                label="Data"
                                value={selectedDate}
                                disabled={!configuracoes}
                                onChange={date => {
                                    const d = date ? new Date(date as any) : null
                                    setSelectedDate(d)

                                    setSlotSelecionado("")
                                    setPeriodos([])

                                    setCarrinho({
                                        ...carrinho,
                                        dataAgendamento: undefined,
                                        slot_inicio: undefined,
                                    })

                                    if (d) handleBuscarPeriodos(d)
                                }}
                                minDate={minDate}
                                maxDate={maxDate}
                                shouldDisableDate={date => {
                                    if (!date) return false
                                    const d = startOfDay(date as any)
                                    return d < minDate || d > maxDate
                                }}
                            />
                        </MuiPickersUtilsProvider>
                    </Grid>

                    <Grid item xs={12} sm={6} md={6}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel id="label-agendamento">Horário de agendamento</InputLabel>

                            <Tooltip
                                title={
                                    !selectedDate
                                        ? "Selecione uma data para ver os horários"
                                        : !slotsPossiveis.length
                                        ? "Não temos agendamento para esse dia"
                                        : ""
                                }
                                disableHoverListener={!!slotsPossiveis.length && !!selectedDate}
                            >
                                <span>
                                    <Select
                                        label="Horário de agendamento"
                                        labelId="label-agendamento"
                                        value={slotSelecionado}
                                        disabled={!selectedDate || !slotsPossiveis.length}
                                        onChange={handleChangeSlot}
                                        fullWidth
                                    >
                                        {slotsPossiveis
                                            .filter(s => {
                                                const hoje = startOfDay(new Date())
                                                const isHoje =
                                                    !!selectedDate &&
                                                    startOfDay(selectedDate).getTime() ===
                                                        hoje.getTime()

                                                return isHoje
                                                    ? s.inicio_minutos > totalMinutosAgora
                                                    : true
                                            })
                                            .map(s => (
                                                <MenuItem key={s.id} value={s.id}>
                                                    {getTime(s.inicio_minutos)} -{" "}
                                                    {getTime(s.fim_minutos)}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </span>
                            </Tooltip>
                        </FormControl>
                    </Grid>
                </Grid>
            )}

            <Grid container justifyContent="center">
                {!isAgendamento ? (
                    <Button
                        onClick={() => {
                            handleBuscarConfiguracoes()
                            setIsAgendamento(true)
                        }}
                        variant="contained"
                        className={classes.agendar}
                    >
                        Agendar pedido
                    </Button>
                ) : (
                    <Button
                        onClick={() => {
                            setCarrinho({
                                ...carrinho,
                                dataAgendamento: undefined,
                                slot_inicio: undefined,
                            })

                            setSlotSelecionado("")
                            setSelectedDate(null)
                            setPeriodos([])
                            setIsAgendamento(false)
                        }}
                        variant="contained"
                        className={classes.cancelar}
                    >
                        Cancelar
                    </Button>
                )}
            </Grid>
        </div>
    )
}
